/**
 * src/lib/uploadManager.js — Upload Manager singleton
 * Drains the IndexedDB voice queue by uploading chunks to the server.
 * Supports exponential backoff, concurrent uploads, and offline detection.
 */

import { apiFetch, API_BASE } from './api/client';
import {
  getPendingChunks,
  updateChunkStatus,
  getChunksByRecording,
  updateRecording,
} from './voiceQueue';

const VOICE_URL = `${API_BASE}/ai`;

/**
 * Convert a Blob to a base64-encoded string (without data URI prefix).
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Monotonic id counter for image-upload jobs. Test-friendly: cleared by
// clearImageJobs() so each test starts at a known offset.
let _imageJobSeq = 0;

class UploadManager {
  constructor() {
    this.isRunning = false;
    this.concurrency = 0;
    this.maxConcurrency = 2;
    this.consecutiveFailures = 0;
    this.backoffMs = 5000;
    this.maxBackoffMs = 60000;
    this.maxRetries = 10;
    this.listeners = {}; // event name → Set of callbacks
    this.pollTimer = null;

    // PUNCH-LIST B4: image-upload jobs run independently of the voice queue.
    // Stored as Map<jobId, ImageJob>. Each enqueue spawns a fire-and-forget
    // task that mutates the job and emits 'image-job:update' on every state
    // change, so React subscribers can render per-row progress.
    this.imageJobs = new Map();

    // Bound handlers for add/removeEventListener symmetry
    this._onOnline = () => {
      this.consecutiveFailures = 0;
      this.backoffMs = 5000;
      this.poll();
    };
    this._onOffline = () => {
      // pause — poll() will early-return when !navigator.onLine
    };
  }

  // ── Lifecycle ──────────────────────────────────────────────────────

  start() {
    if (this.isRunning) return; // idempotent
    this.isRunning = true;
    window.addEventListener('online', this._onOnline);
    window.addEventListener('offline', this._onOffline);
    this.poll();
  }

  stop() {
    this.isRunning = false;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    window.removeEventListener('online', this._onOnline);
    window.removeEventListener('offline', this._onOffline);
  }

  // ── Poll loop ──────────────────────────────────────────────────────

  async poll() {
    if (!this.isRunning || !navigator.onLine || this.concurrency >= this.maxConcurrency) {
      return;
    }

    try {
      const limit = this.maxConcurrency - this.concurrency;
      const pending = await getPendingChunks(limit);

      if (!pending || pending.length === 0) {
        this.emit('queue-empty');
        this._scheduleNextPoll(10000);
        return;
      }

      for (const chunk of pending) {
        if (this.concurrency >= this.maxConcurrency) break;
        this.uploadChunk(chunk); // fire-and-forget — runs concurrently
      }

      const delay = this.consecutiveFailures > 0 ? this.backoffMs : 5000;
      this._scheduleNextPoll(delay);
    } catch (err) {
      console.warn('[UploadManager] poll error:', err.message);
      this._scheduleNextPoll(this.backoffMs);
    }
  }

  _scheduleNextPoll(delayMs) {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = setTimeout(() => this.poll(), delayMs);
  }

  // ── Upload a single chunk ─────────────────────────────────────────

  async uploadChunk(chunk) {
    this.concurrency++;
    try {
      await updateChunkStatus(chunk.id, 'uploading');

      // Convert Blob to base64 if present
      let audioData = chunk.audio_blob;
      if (audioData instanceof Blob) {
        audioData = await blobToBase64(audioData);
      }

      const result = await apiFetch(VOICE_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: 'voice.upload_chunk',
          recording_id: chunk.recording_id,
          chunk_index: chunk.chunk_index,
          audio: audioData,
          mime_type: chunk.mime_type,
          duration_ms: chunk.duration_ms,
        }),
      });

      if (result && result.success !== false) {
        await updateChunkStatus(chunk.id, 'uploaded');
        this.consecutiveFailures = 0;
        this.backoffMs = 5000;
        this.emit('chunk-uploaded', chunk);
        await this.checkRecordingComplete(chunk.recording_id);
      } else {
        const errMsg = result?.error || 'Upload failed';
        await updateChunkStatus(chunk.id, 'failed', errMsg);
        this.consecutiveFailures++;
        this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
        if (this.consecutiveFailures >= this.maxRetries) {
          this.emit('offline');
        }
      }
    } catch (err) {
      try {
        await updateChunkStatus(chunk.id, 'failed', err.message);
      } catch {
        // ignore status update failure
      }
      this.consecutiveFailures++;
      this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
      if (this.consecutiveFailures >= this.maxRetries) {
        this.emit('offline');
      }
    } finally {
      this.concurrency--;
    }
  }

  // ── Recording completion check ────────────────────────────────────

  async checkRecordingComplete(recordingId) {
    const chunks = await getChunksByRecording(recordingId);
    const allUploaded = chunks.length > 0 && chunks.every((c) => c.status === 'uploaded');
    if (allUploaded) {
      await this.finalizeRecording(recordingId);
    }
  }

  async finalizeRecording(recordingId) {
    const result = await apiFetch(VOICE_URL, {
      method: 'POST',
      body: JSON.stringify({
        action: 'voice.finalize_recording',
        recording_id: recordingId,
      }),
    });

    if (result && result.voice_record_id) {
      await updateRecording(recordingId, {
        status: 'processing',
        voice_record_id: result.voice_record_id,
      });
    }

    this.emit('recording-finalized', { recordingId });
  }

  // ── Image upload jobs (PUNCH-LIST B4) ─────────────────────────────
  //
  // Generic, transport-agnostic queue for media uploads. The `uploader`
  // callback is injected per job — production uses uploadMaterialPhoto;
  // tests pass a mock. Jobs run concurrently (no awaits in enqueue) so
  // the calling event handler returns synchronously.

  enqueueImageUpload({ file, materialId, uploader, onSuccess, onError }) {
    if (!file || !materialId || typeof uploader !== 'function') {
      throw new Error('[uploadManager] enqueueImageUpload requires file, materialId, uploader');
    }
    const jobId = `img-${Date.now()}-${++_imageJobSeq}`;
    const job = {
      id: jobId,
      file,
      materialId,
      status: 'queued',
      progress: 0,
      error: null,
      result: null,
      createdAt: Date.now(),
      _onSuccess: onSuccess,
      _onError: onError,
    };
    this.imageJobs.set(jobId, job);
    // Fire-and-forget — DO NOT await. The event handler that called us
    // returns immediately so the UI never blocks.
    this._runImageJob(jobId, uploader);
    return jobId;
  }

  async _runImageJob(jobId, uploader) {
    const job = this.imageJobs.get(jobId);
    if (!job) return;
    this._patchImageJob(jobId, { status: 'uploading', progress: 0 });
    try {
      const result = await uploader(job.materialId, job.file);
      this._patchImageJob(jobId, { status: 'success', progress: 100, result });
      try { job._onSuccess?.(result); } catch (e) { console.warn('[uploadManager] onSuccess threw', e); }
    } catch (err) {
      this._patchImageJob(jobId, { status: 'failed', error: err?.message || String(err) });
      try { job._onError?.(err); } catch (e) { console.warn('[uploadManager] onError threw', e); }
    }
  }

  _patchImageJob(jobId, patch) {
    const job = this.imageJobs.get(jobId);
    if (!job) return;
    Object.assign(job, patch);
    this.emit('image-job:update', { jobId, materialId: job.materialId, job });
  }

  getImageJobsFor(materialId) {
    const out = [];
    for (const job of this.imageJobs.values()) {
      if (job.materialId === materialId) out.push(job);
    }
    return out;
  }

  /**
   * Subscribe to all updates affecting `materialId`. Returns an unsubscribe.
   * Callback receives the current snapshot of jobs for that material.
   */
  subscribeImageJobs(materialId, callback) {
    const handler = ({ materialId: mid }) => {
      if (mid !== materialId) return;
      callback(this.getImageJobsFor(materialId));
    };
    this.on('image-job:update', handler);
    return () => this.off('image-job:update', handler);
  }

  /** Clear all image jobs. Test helper. */
  clearImageJobs() {
    this.imageJobs.clear();
    _imageJobSeq = 0;
  }

  // ── Event emitter ─────────────────────────────────────────────────

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = new Set();
    }
    this.listeners[event].add(callback);
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners[event]) {
      for (const cb of this.listeners[event]) {
        try {
          cb(data);
        } catch (err) {
          console.warn(`[UploadManager] listener error for ${event}:`, err);
        }
      }
    }
  }
}

export { UploadManager, blobToBase64 };
export const uploadManager = new UploadManager();
